import React, { useContext, useEffect, useState } from "react";

import ApplicationContext from "../../../context/ApplicationContext";

import {
  Button,
  Form,
  FormField,
  Message,
  MessageHeader,
} from "semantic-ui-react";
import {
  addSnookerTable,
  getSnookerTables,
  updateSnookerTable,
} from "../../../services/Service";
import { useNavigate, useParams } from "react-router-dom";
import { generateGUID } from "../../../helpers/Helpers";
import { Container } from "react-bootstrap";
import { ISnookerTable } from "../../../types/ISnookerTable";

export const SnookerTableEditor: React.FC = (): JSX.Element => {
  const context = useContext(ApplicationContext);

  const defaultTableStyle =
    '{"ButtonWidth":"150","ButtonHeight":"50","FontSize":"16","RunningColour":"#f50505","StoppedColour":"#f50505","AdvancePayColour":"#21ff7a","AlarmColour":"#fae43c","TextColourRunning":"#ffffff","TextColourStopped":"#ffffff","TextColourAdvancePay":"#ffffff","TextColourAlarm":"#ffffff"}';

  const { id } = useParams();

  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const [uuid, setUuid] = useState("");
  const [description, setDescription] = useState("Snooker 1");
  const [position, setPosition] = useState("1");
  const [peekPrice, setPeekPrice] = useState("1");
  const [offPeekPrice, setOffPeekPrice] = useState("1");
  const [peekStart, setPeekStart] = useState("01:00");
  const [peekEnd, setPeekEnd] = useState("01:00");
  const [tableStyle, setTableStyle] = useState(defaultTableStyle);

  const navigate = useNavigate();

  async function getTables() {
    setLoading(true);
    const response = await getSnookerTables(
      context.AuthenticatedUser.getToken()
    );

    if (!response) {
      context.ApplicationError.setHasError(true);
      return;
    }

    context.Snooker.setData(response);

    setLoading(false);
  }

  useEffect(() => {
    getTables();
    setEditMode(false);

    if (id === undefined) {
      return;
    }

    setUuid(id);
    edit(id);
  }, []);

  function formatTime(dt: Date) {
    return "01:00";
  }
  function formatDateTime(t: string) {
    const dt = new Date();
    return dt;
  }

  function edit(myId: string) {
    setEditMode(true);

    const tables = context.Snooker.getData();
    const table = tables.find((t) => t.uuid === myId);

    if (table === undefined) {
      return;
    }

    setUuid(myId);

    setDescription(table.description);
    setPosition(table.position.toString());
    setPeekPrice(table.peekPrice.toFixed(2));
    setOffPeekPrice(table.offPeekPrice.toFixed(2));
    setPeekStart(formatTime(table.peekStart));
    setPeekEnd(formatTime(table.peekStart));
    setTableStyle(table.style);
  }

  function validateMe() {
    return true;
  }

  async function updateTable(table: ISnookerTable) {
    const tb = context.Snooker.getData().find((t) => t.uuid === uuid);

    if (tb === undefined) {
      return;
    }

    table.id = tb.id;
    table.uuid = tb.uuid;

    const created = await updateSnookerTable(
      context.AuthenticatedUser.getToken(),
      table
    );

    if (!created) {
      setError(true);
      setSaving(false);
      return;
    }

    navigate("/SnookerTables");
  }

  async function createSnookerTable() {
    if (!validateMe()) {
      return;
    }

    setSaving(true);

    const table: ISnookerTable = {
      id: 0,
      uuid: generateGUID(),
      position: Number(position),
      description: description,
      isDeleted: false,
      peekPrice: Number(peekPrice),
      offPeekPrice: Number(offPeekPrice),
      peekStart: formatDateTime(peekStart),
      peekEnd: formatDateTime(peekEnd),
      hasStarted: false,
      startTime: new Date(),
      receiptData: "",
      advancePayment: 0,
      reminderInMinutes: 0,
      style: tableStyle,
    };

    if (editMode) {
      updateTable(table);
      return;
    }

    const created = await addSnookerTable(
      context.AuthenticatedUser.getToken(),
      table
    );

    if (!created) {
      setError(true);
      setSaving(false);
      return;
    }

    setSaving(false);
    navigate("/SnookerTables");
  }

  const editor = (): JSX.Element => (
    <>
      <Container>
        <h3>{editMode ? <>Update table</> : <>Add table</>}</h3>
        <hr />

        {error ? (
          <Message negative>
            <MessageHeader>Saving error</MessageHeader>
            <p>
              Error in saving table. Please try again <br /> If the error
              persists please contact support
            </p>
          </Message>
        ) : (
          <></>
        )}

        <div style={{ paddingTop: 10 }}>
          <Form>
            <FormField>
              <label>Description</label>
              <input
                placeholder="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </FormField>
            <FormField>
              <label>Position</label>
              <input
                placeholder="Position"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
              />
            </FormField>
            <FormField>
              <label>Peek price</label>
              <input
                placeholder="Peek Price"
                value={peekPrice}
                onChange={(e) => setPeekPrice(e.target.value)}
              />
            </FormField>
            <FormField>
              <label>Off peek price</label>
              <input
                placeholder="Off peek price"
                value={offPeekPrice}
                onChange={(e) => setOffPeekPrice(e.target.value)}
              />
            </FormField>
            <FormField>
              <label>Peek start</label>
              <input
                type="time"
                placeholder="Peek start"
                value={peekStart}
                onChange={(e) => setPeekPrice(e.target.value)}
              />
            </FormField>
            <FormField>
              <label>Peek end</label>
              <input
                type="time"
                placeholder="Peek end"
                value={peekEnd}
                onChange={(e) => setPeekEnd(e.target.value)}
              />
            </FormField>
            <FormField>
              <label>Style</label>
              <textarea
                placeholder=""
                value={tableStyle}
                onChange={(e) => setTableStyle(e.target.value)}
              />
            </FormField>

            {saving ? (
              <Button type="submit" primary loading></Button>
            ) : (
              <Button
                type="submit"
                primary
                onClick={() => createSnookerTable()}
              >
                {!editMode ? "Create" : "Update"}
              </Button>
            )}
          </Form>
        </div>
      </Container>
    </>
  );

  return (
    <>
      <>{editor()}</>
    </>
  );
};
